import { v4 as uuidv4 } from 'uuid';
import { LineItemTypeOptions, TAX_CODE_OTHER_EXEMPT } from '../../ocr/types/OcrTypes';
import { createAccountingVendor, getCatalogProductForSku, searchCatalogByPackageID } from '../../ocr/network/OcrNetwork';
import { fetchUnits } from '../../ocr/network/OcrLoadNetwork';
import { checkInputFieldEquality, valueToFixedNumber, valueToFixedString } from '../../ocr/logic/OcrInvoiceHelpers';
/**
 *
 */
export class BaseOcrDataPageFunctions {
    /**
     * Get the subtotal for the line item (total before discounts and tax are applied)
     */
    getLineItemSubtotal(ocrLineItem) {
        var _a, _b, _c, _d, _e;
        if (!ocrLineItem) {
            return 0;
        }
        const lineItem = ocrLineItem.primaryItemData;
        if (!lineItem) {
            return 0;
        }
        const quantity = parseFloat((_a = lineItem === null || lineItem === void 0 ? void 0 : lineItem.quantity) === null || _a === void 0 ? void 0 : _a.toString()) || 0;
        const weight = parseFloat((_b = lineItem === null || lineItem === void 0 ? void 0 : lineItem.weight) === null || _b === void 0 ? void 0 : _b.toString()) || 0;
        const displayedUnitPrice = valueToFixedNumber((_c = ocrLineItem === null || ocrLineItem === void 0 ? void 0 : ocrLineItem.primaryItemData) === null || _c === void 0 ? void 0 : _c.unitPrice);
        const unitDeposit = valueToFixedNumber((_d = ocrLineItem === null || ocrLineItem === void 0 ? void 0 : ocrLineItem.primaryItemData) === null || _d === void 0 ? void 0 : _d.unitDeposit);
        let weightAdjustedUnitPrice = displayedUnitPrice;
        if ((_e = ocrLineItem === null || ocrLineItem === void 0 ? void 0 : ocrLineItem.linkedCatalogData) === null || _e === void 0 ? void 0 : _e.quantity) {
            weightAdjustedUnitPrice /= ocrLineItem.linkedCatalogData.quantity;
        }
        weightAdjustedUnitPrice = valueToFixedNumber(weightAdjustedUnitPrice);
        const { hasFinalSalesQuantitySet } = lineItem;
        const isWeightDifferentThanOriginal = lineItem.weight !== lineItem.originalWeight;
        // if this order line item has had its finalSalesQuantity set then we ignore the quantity field entirely.
        // Further, if the weight isn't the original weight then we ignore quantity. The reason being when we send this to
        // be-core it will set the finalSalesQuantity value which will ignore quantity in the totals. We want the totals
        // displayed to reflect what will happen in be-core.
        if ((hasFinalSalesQuantitySet || isWeightDifferentThanOriginal) && weight > 0) {
            return (weightAdjustedUnitPrice + unitDeposit) * weight;
        }
        return parseFloat(valueToFixedString((displayedUnitPrice + unitDeposit) * quantity));
    }
    /**
     * Get the subtotal with the discount subtracted
     */
    getSubtotalWithDiscounts(ocrLineItem) {
        var _a;
        return this.getLineItemSubtotal(ocrLineItem) - Math.abs(valueToFixedNumber((_a = ocrLineItem === null || ocrLineItem === void 0 ? void 0 : ocrLineItem.primaryItemData) === null || _a === void 0 ? void 0 : _a.discount));
    }
    /**
     * Get the total for the line item (value after discounts and taxes are applied)
     */
    getLineItemTotal(ocrLineItem) {
        var _a;
        const lineItem = ocrLineItem.primaryItemData;
        const tax = ((_a = lineItem === null || lineItem === void 0 ? void 0 : lineItem.tax) === null || _a === void 0 ? void 0 : _a.dollarAmount) || 0;
        return valueToFixedNumber(this.getSubtotalWithDiscounts(ocrLineItem) + tax);
    }
    /**
     * Given an confidence group from the OCR invoice data, return it as a number
     */
    getItemConfidenceAsNumber(confidenceGroup, notFoundValue = 0) {
        const value = parseFloat(confidenceGroup === null || confidenceGroup === void 0 ? void 0 : confidenceGroup.value);
        return value || notFoundValue;
    }
    /**
     * Reads in data from the OCR database and parses it into a LineItem type
     * @returns {LineItem[]} which is then used by the OCR system
     */
    getLineItemsFromInvoiceData(ocrParsedLineItems) {
        var _a, _b, _c, _d, _e, _f, _g;
        const lineItems = [];
        if (ocrParsedLineItems) {
            for (let i = 0; i < ocrParsedLineItems.length; i++) {
                const curProduct = ocrParsedLineItems[i];
                const tax = { dollarAmount: 0, percent: 0 };
                let taxIsSet = false;
                // in this case the tax amount has been saved, so read it instead
                if (curProduct === null || curProduct === void 0 ? void 0 : curProduct.tax) {
                    const parsedTotal = this.getItemConfidenceAsNumber(curProduct === null || curProduct === void 0 ? void 0 : curProduct.amount);
                    tax.dollarAmount = this.getItemConfidenceAsNumber(curProduct === null || curProduct === void 0 ? void 0 : curProduct.tax);
                    const calculatedSubtotal = (parsedTotal - tax.dollarAmount);
                    taxIsSet = true;
                    // protect against divide by zero NaN problems.
                    if (calculatedSubtotal <= 0) {
                        tax.percent = 0;
                    }
                    else {
                        tax.percent = (tax.dollarAmount / calculatedSubtotal) * 100;
                    }
                }
                else { // if tax hasn't been saved, try to calculate it google doesn't parse tax by default. I will try to uptrain the model and add the tax as a custom field for us.
                    const unitPrice = this.getItemConfidenceAsNumber(curProduct === null || curProduct === void 0 ? void 0 : curProduct.unitPrice);
                    const quantity = this.getItemConfidenceAsNumber(curProduct === null || curProduct === void 0 ? void 0 : curProduct.quantity);
                    const weight = this.getItemConfidenceAsNumber(curProduct === null || curProduct === void 0 ? void 0 : curProduct.weight);
                    const total = this.getItemConfidenceAsNumber(curProduct === null || curProduct === void 0 ? void 0 : curProduct.amount);
                    let calculatedNetPrice = unitPrice * quantity;
                    if (weight) {
                        calculatedNetPrice = weight * unitPrice;
                    }
                    const calculatedTaxAmount = total - calculatedNetPrice;
                    let calculatedTaxPercent = 0;
                    if (!isNaN(calculatedTaxAmount) && calculatedTaxAmount > 0) {
                        calculatedTaxPercent = calculatedNetPrice / calculatedTaxAmount;
                    }
                    tax.dollarAmount = calculatedTaxAmount;
                    tax.percent = calculatedTaxPercent;
                }
                // round the tax dollar and percent off to 2 decimals
                tax.dollarAmount = valueToFixedNumber(tax.dollarAmount, 2);
                tax.percent = valueToFixedNumber(tax.percent, 2);
                lineItems.push({
                    name: ((_a = curProduct === null || curProduct === void 0 ? void 0 : curProduct.description) === null || _a === void 0 ? void 0 : _a.value) || '',
                    sku: ((_b = curProduct === null || curProduct === void 0 ? void 0 : curProduct.productCode) === null || _b === void 0 ? void 0 : _b.value) || '',
                    unit: ((_c = curProduct === null || curProduct === void 0 ? void 0 : curProduct.unit) === null || _c === void 0 ? void 0 : _c.value) || '',
                    quantity: +((_d = curProduct === null || curProduct === void 0 ? void 0 : curProduct.quantity) === null || _d === void 0 ? void 0 : _d.value) || 0,
                    unitPrice: +valueToFixedString(((_e = curProduct === null || curProduct === void 0 ? void 0 : curProduct.unitPrice) === null || _e === void 0 ? void 0 : _e.value) || 0),
                    total: parseFloat(valueToFixedString(((_f = curProduct === null || curProduct === void 0 ? void 0 : curProduct.amount) === null || _f === void 0 ? void 0 : _f.value) || 0)),
                    weight: Number(((_g = curProduct === null || curProduct === void 0 ? void 0 : curProduct.weight) === null || _g === void 0 ? void 0 : _g.value) || 0),
                    removedFromOcrInvoice: (curProduct === null || curProduct === void 0 ? void 0 : curProduct.isRemoved) === true,
                    taxCode: '99999',
                    packageID: null,
                    tax,
                    taxIsSet,
                    glCodeID: curProduct === null || curProduct === void 0 ? void 0 : curProduct.glCodeID,
                    unitDeposit: curProduct === null || curProduct === void 0 ? void 0 : curProduct.unitDeposit,
                    discount: this.getItemConfidenceAsNumber(curProduct === null || curProduct === void 0 ? void 0 : curProduct.discountAmount),
                });
            }
        }
        return lineItems;
    }
    /**
     * Updates the tax percent and recalculates the total. Does not modify the tax dollar amount
     */
    updateLineItemTax(lineItem, globalTaxPercent) {
        lineItem.primaryItemData.tax.percent = globalTaxPercent * 100;
        lineItem.primaryItemData.total = this.getLineItemTotal(lineItem);
        lineItem.matchResult = this.getLineItemMatchResult(lineItem);
    }
    /**
     * Returns an "empty" OcrMatchedLineItem which is an object that contains matching data from the OCR and Order data
     */
    makeEmptyLineItem(type = LineItemTypeOptions.PRODUCT) {
        const emptyLineItem = this.getEmptyLineItemData(type);
        return {
            primaryItemData: Object.assign({}, emptyLineItem),
            secondaryItemData: Object.assign({}, emptyLineItem),
            matchResult: 'new',
            isRemoved: false,
            reactKey: uuidv4(),
            defaultLineItem: Object.assign({}, emptyLineItem),
            type,
            confidence: {
                name: 1,
                sku: 1,
                unit: 1,
                quantity: 1,
                unitPrice: 1,
                weight: 1,
            },
        };
    }
    /**
     * Returns empty line item data for a given type
     * @param type - The type of line item to return
     * @returns {LineItem}
     */
    getEmptyLineItemData(type = LineItemTypeOptions.PRODUCT) {
        const baseLineItemData = {
            name: '',
            sku: '',
            unit: '',
            quantity: 0,
            unitPrice: 0,
            weight: 0,
            taxCode: '99999',
            taxable: false,
            total: 0,
            tax: { dollarAmount: 0, percent: 0 },
            type,
        };
        switch (type) {
            case LineItemTypeOptions.PRODUCT:
                return baseLineItemData;
            case LineItemTypeOptions.TAX:
                return Object.assign(Object.assign({}, baseLineItemData), { name: 'Tax', unit: 'case', quantity: 1 });
            case LineItemTypeOptions.FEE:
                return Object.assign(Object.assign({}, baseLineItemData), { name: 'Fee', unit: 'case', quantity: 1 });
        }
    }
    /**
     * Returns an empty "top level object" which is used for the totals, buyer info, supplier info, etc.
     * @returns {TopLevelOcrData}
     */
    getEmptyTopLevelObject() {
        return {
            invoiceNumber: '',
            supplierName: '',
            externalReferenceID: '',
            buyerName: '',
            issueDate: undefined,
            dueDate: undefined,
            subtotal: 0,
            tax: 0,
            total: 0,
            totalDiscount: 0,
            accountingClassID: '',
            accountingVendorID: null,
        };
    }
    /**
     * For a given list of line items, a buyer URL safe, and a catalog ID - get back a new list of OcrMatchedLineItems that has their linkedCatalogData set
     * @param items A list of items to set linkedCatalogData in
     * @param buyerUrlSafeKey The URLSafeKey of the buyer
     * @param catalogID The catalog ID of the OCR invoice which is known from the buyer/supplier relationship in catalog
     * @returns a new list of OcrMatchedLineItems that has their linkedCatalogData set
     */
    async fetchPackageForLineItems(items, catalogID, priceListID) {
        const newLineItems = [...items];
        const promises = items
            .filter((lineItem) => !!lineItem.primaryItemData.sku)
            .map((lineItem) => getCatalogProductForSku(lineItem.primaryItemData.sku, catalogID, priceListID));
        const units = await (await fetchUnits()).units;
        const promiseResults = await Promise.allSettled(promises);
        for (let i = 0; i < promiseResults.length; i++) {
            const settledValue = promiseResults[i];
            if (settledValue.status === 'fulfilled') {
                const product = settledValue.value;
                if (product) {
                    const unit = this.getValidUnit(product.unit, units);
                    newLineItems[i].linkedCatalogData = {
                        id: product === null || product === void 0 ? void 0 : product.id,
                        description: product === null || product === void 0 ? void 0 : product.description,
                        availability: product === null || product === void 0 ? void 0 : product.availability,
                        sku: product === null || product === void 0 ? void 0 : product.sku,
                        quantity: product === null || product === void 0 ? void 0 : product.quantity,
                        unit,
                        prices: [...product.prices],
                        taxCode: product.taxCode,
                    };
                    // also set their units
                    if (unit) {
                        newLineItems[i].primaryItemData.unit = unit;
                    }
                    if (product.taxCode) {
                        if (product.taxCode === 'Fully Taxable') {
                            newLineItems[i].primaryItemData.taxCode = '';
                        }
                        else {
                            newLineItems[i].primaryItemData.taxCode = product.taxCode;
                        }
                    }
                    if (product.taxCode === TAX_CODE_OTHER_EXEMPT.value && !newLineItems[i].primaryItemData.tax) {
                        newLineItems[i].primaryItemData.tax = { dollarAmount: 0, percent: 0 };
                    }
                    if (!newLineItems[i].primaryItemData.packageID && product) {
                        newLineItems[i].primaryItemData.packageID = product === null || product === void 0 ? void 0 : product.id;
                    }
                }
            }
        }
        return newLineItems;
    }
    /**
     * Unit must be a part of units to be considered valid, or be a close match.
     * @param unit - Unit to validate against
     * @param units - Must be all lowercase
     */
    getValidUnit(unit, units) {
        let lowerCaseUnit = unit.toLowerCase();
        // Check for some common short forms
        switch (lowerCaseUnit) {
            case 'ea':
                lowerCaseUnit = 'each';
                break;
            case 'cs':
                lowerCaseUnit = 'case';
                break;
            case 'pk':
            case 'pck':
                lowerCaseUnit = 'pack';
                break;
            case 'doz':
            case 'dz':
                lowerCaseUnit = 'dozen';
                break;
            case 'l':
                lowerCaseUnit = 'litre';
                break;
            case 'gal':
                lowerCaseUnit = 'gallon';
                break;
            case 'lbs':
                lowerCaseUnit = 'lb';
                break;
            default:
                break;
        }
        const validUnit = units.includes(lowerCaseUnit) ? lowerCaseUnit : undefined;
        return validUnit;
    }
    /**
     * Fetch category for line items to use category ID for GL Code mappings.
     */
    async fetchCategoryForLineItems(items, catalogID, priceListID) {
        const newLineItems = [...items];
        const promises = items
            .filter((lineItem) => !!lineItem.primaryItemData.sourceUUID)
            .map((lineItem) => searchCatalogByPackageID(catalogID, lineItem.primaryItemData.sourceUUID, priceListID));
        const promiseResults = await Promise.allSettled(promises);
        for (let i = 0; i < promiseResults.length; i++) {
            const settledValue = promiseResults[i];
            if (settledValue.status === 'fulfilled') {
                const product = settledValue.value[0];
                if (product && !newLineItems[i].primaryItemData.categoryID) {
                    newLineItems[i].primaryItemData.categoryID = product.categoryID;
                }
            }
        }
        return newLineItems;
    }
    /**
     * Checks if GL Code is empty then set if from the Categories mapping
     */
    setGlCodesFromCategoryMappings(packageItems, glMappings) {
        var _a, _b;
        for (let i = 0; i < packageItems.length; i++) {
            // GL Code is set from the SKU mappings above if found
            // otherwise, it is set from the product categories mappings
            if (!((_a = packageItems[i].primaryItemData) === null || _a === void 0 ? void 0 : _a.glCodeID)) {
                packageItems[i].primaryItemData.glCodeID = glMappings === null || glMappings === void 0 ? void 0 : glMappings[(_b = packageItems[i].primaryItemData) === null || _b === void 0 ? void 0 : _b.categoryID];
            }
        }
    }
    /**
     * Get the MatchResult for this line item. Eg if the value has changed it will be 'userChange'
     * @param lineItem The line item to calculate the match result for
     * @returns {MatchResults}
     */
    getLineItemMatchResult(lineItem) {
        const isEqualToDefault = this.areLineItemsEqual(lineItem.primaryItemData, lineItem.defaultLineItem);
        let { matchResult } = lineItem;
        if (!isEqualToDefault && lineItem.defaultMatchResult !== 'new' && lineItem.defaultMatchResult !== 'onlyOcr') {
            matchResult = 'userChange';
        }
        else if (matchResult === 'userChange') {
            matchResult = lineItem.defaultMatchResult;
        }
        return matchResult;
    }
    /**
     *
     */
    areLineItemsEqual(a, b) {
        var _a, _b, _c, _d;
        const keys = ['name', 'sku', 'glCodeID', 'unit', 'quantity', 'unitPrice', 'total', 'weight', 'taxCode', 'tax'];
        for (let i = 0; i < keys.length; i++) {
            const curKey = keys[i];
            if (curKey === 'tax') {
                return ((_a = a.tax) === null || _a === void 0 ? void 0 : _a.dollarAmount) === ((_b = b.tax) === null || _b === void 0 ? void 0 : _b.dollarAmount) && ((_c = a.tax) === null || _c === void 0 ? void 0 : _c.percent) === ((_d = b.tax) === null || _d === void 0 ? void 0 : _d.percent);
            }
            if (!checkInputFieldEquality(a[curKey], b[curKey])) {
                return false;
            }
        }
        return true;
    }
    /**
     * finds all skus across all line items that appear more than once
     * @returns an array of strings that appear more than once. This is a set and only contains each duplicate entry one time.
     */
    findSkuDuplicates(lineItems) {
        if (!lineItems || lineItems.length === 0) {
            return [];
        }
        const foundSet = new Set();
        const duplicateArray = [];
        lineItems.forEach((curLineItem) => {
            const { sku } = curLineItem.primaryItemData;
            if (!curLineItem.isRemoved && sku) {
                if (foundSet.has(sku) && !duplicateArray.includes(sku)) {
                    duplicateArray.push(sku);
                }
                foundSet.add(sku);
            }
        });
        return duplicateArray;
    }
    /**
     *
     */
    async createNewAccountingVendor(platform, buyerUrlsafeKey, supplierData) {
        var _a;
        const emails = (_a = supplierData.email) === null || _a === void 0 ? void 0 : _a.split(',');
        const primaryEmail = emails ? emails[0] : '';
        return createAccountingVendor(platform, buyerUrlsafeKey, supplierData.key, supplierData.name, primaryEmail);
    }
}
//# sourceMappingURL=BaseOcrDataPageFunctions.js.map